import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useIsSearchToFeedEnabled } from '@modules/common/hooks';
import {
  ListDashboardsDocument,
  useCreateDashboardMutation,
} from 'generated/graphql';
import { DashboardUseCase } from 'v2/core/entities/dashboardUseCase';
import { Scope } from 'v2/core/entities/scope';
import {
  mapToDashboardUseCase,
  mapToScope,
} from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

const CREATE_SEARCH_DASHBOARD_REFETCH_QUERIES = [ListDashboardsDocument];

export interface CreateSearchDashboardOptions {
  title: string;
  scope: Scope;
  metadata?: string;
}

export const useCreateSearchDashboard = ({
  title,
  scope,
  metadata = '',
}: CreateSearchDashboardOptions) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const [createSearchDashboardMutation, { loading, error, called }] =
    useCreateDashboardMutation({
      variables: {
        tenantID,
        title,
        scope: mapToScope(scope),
        useCase: mapToDashboardUseCase(DashboardUseCase.Search),
        createdBy: userID,
        metadata: metadata,
      },
      refetchQueries: CREATE_SEARCH_DASHBOARD_REFETCH_QUERIES,
      onError: () => {
        showErrorNotification({
          contentComponent: `Failed to save ${
            isSearchToFeedEnabled ? 'Feed' : 'Search'
          }`,
          props: { isClosable: true },
        });
      },
    });

  const status = apolloStatusToQueryStatus(loading, called, false, error);

  return {
    createSearchDashboard: createSearchDashboardMutation,
    isLoading: IsLoading(status),
    error,
  };
};
